import { AirplaneInFlight } from "@phosphor-icons/react";
import moment from "moment";
import { FC } from "react";
import { Link } from "react-router-dom";
import { BookedFlight, TravelInformation } from "../@types";

const FlightLegRow = ({
  leg,
  label,
  accentColor = "text-visaro-orange",
  lineColor = "bg-gray-200",
}: {
  leg: TravelInformation;
  label?: string;
  accentColor?: string;
  lineColor?: string;
}) => (
  <div className="flex flex-col gap-2">
    {label && (
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest shrink-0">
          {label}
        </span>
        <div className="h-[1px] flex-1 bg-gray-100" />
        <span className="text-[10px] font-medium text-gray-400 shrink-0">
          {moment(leg.DepartureDate).format("ddd, DD MMM YYYY")}
        </span>
      </div>
    )}
    <div className="flex items-center gap-3">
      {/* Departure */}
      <div className="flex flex-col items-start min-w-[52px]">
        <span className="text-2xl font-bold font-aeonic text-deepBlue">
          {leg.DepartureTerminalCode}
        </span>
        <span className="text-xs font-bold text-gray-400">
          {moment(leg.DepartureDate).format("HH:mm")}
        </span>
      </div>

      {/* Connector */}
      <div className="flex-1 flex flex-col items-center gap-0.5 min-w-0">
        <div className="flex items-center w-full gap-1.5">
          <div className={`h-[1px] flex-1 ${lineColor} relative`}>
            <div className="absolute left-0 top-1/2 -translate-y-1/2 size-1.5 rounded-full bg-gray-300" />
          </div>
          <AirplaneInFlight className={`size-4 shrink-0 ${accentColor}`} weight="fill" />
          <div className={`h-[1px] flex-1 ${lineColor} relative`}>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 size-1.5 rounded-full bg-gray-300" />
          </div>
        </div>
        <span className="text-[10px] text-gray-400 font-medium truncate max-w-full text-center">
          {leg.OperatorName}
        </span>
      </div>

      {/* Arrival */}
      <div className="flex flex-col items-end min-w-[52px]">
        <span className="text-2xl font-bold font-aeonic text-deepBlue text-right">
          {leg.ArrivalTerminalCode}
        </span>
        <span className="text-xs font-bold text-gray-400">
          {moment(leg.ArrivalDate).format("HH:mm")}
        </span>
      </div>
    </div>

    {/* Terminal names */}
    <div className="flex justify-between text-[11px] text-gray-400 leading-tight">
      <span className="truncate max-w-[44%]">{leg.DepartureTerminal}</span>
      <span className="truncate max-w-[44%] text-right">{leg.ArrivalTerminal}</span>
    </div>
  </div>
);

const BookedFlightsList: FC<{
  bookedFlights: BookedFlight[];
  hideTitle?: boolean;
}> = ({ bookedFlights, hideTitle = false }) => {
  return (
    <div className="flex flex-col gap-6">
      {!hideTitle && (
        <h3 className="text-2xl font-medium">Your Booked Flights</h3>
      )}
      <div className="grid grid-cols-1 gap-6">
        {bookedFlights.map((flight) => {
          const travelInfos = flight.copy?.TravelInformations ?? [];
          const outbound = travelInfos[0];
          const returnLeg = travelInfos.length > 1 ? travelInfos[travelInfos.length - 1] : null;
          const isReturn = travelInfos.length > 1;
          const passengerCount =
            flight.copy?.PassengerDetails?.AirTravellers?.length ??
            flight.air_travelers?.length ??
            0;
          const confirmationCodes = (flight.confirmation_code ?? "")
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean);
          const displayRef = confirmationCodes[0] || null;

          if (!outbound) return null;

          return (
            <Link
              to={`/aviation/flight/${flight.id}`}
              key={flight.id}
              className="group relative flex flex-col md:flex-row items-stretch bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-visaro-orange/30 transition-all duration-300 overflow-hidden"
            >
              {/* Accent Line */}
              <div className="absolute top-0 left-0 w-full h-1 md:w-1 md:h-full bg-visaro-orange" />

              <div className="flex-1 p-6 flex flex-col gap-4">
                {/* Card Header */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-3 py-1 bg-deepBlue text-white text-xs font-bold rounded-full uppercase tracking-wider">
                      {isReturn ? "Return" : "One Way"}
                    </span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-bold rounded-full uppercase tracking-wider">
                      {outbound.TicketClass || "Economy"}
                    </span>
                    <span className="px-3 py-1 bg-gray-50 text-gray-500 text-xs font-medium rounded-full capitalize">
                      {flight.type}
                    </span>
                  </div>
                  {displayRef && (
                    <div className="text-xs font-bold text-gray-400 font-aeonic tracking-widest uppercase shrink-0">
                      {displayRef}
                    </div>
                  )}
                </div>

                {/* Flight Legs */}
                <div className="flex flex-col gap-3">
                  <FlightLegRow
                    leg={outbound}
                    label={isReturn ? "Outbound" : undefined}
                  />
                  {!isReturn && (
                    <p className="text-xs text-gray-400 font-medium">
                      {moment(outbound.DepartureDate).format("ddd, MMM DD, YYYY")}
                    </p>
                  )}

                  {returnLeg && (
                    <>
                      <div className="border-t border-dashed border-gray-100" />
                      <FlightLegRow leg={returnLeg} label="Return" />
                    </>
                  )}
                </div>

                {/* Footer */}
                <div className="pt-3 border-t border-gray-50 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">
                    {passengerCount} Traveler{passengerCount !== 1 ? "s" : ""}
                  </span>
                  {confirmationCodes.length > 1 && (
                    <span className="text-xs text-gray-400 font-medium tracking-wide">
                      {confirmationCodes.join(" · ")}
                    </span>
                  )}
                </div>
              </div>

              {/* Ticket Stub Sidebar */}
              <div className="hidden md:flex flex-col border-l border-dashed border-gray-200 p-6 bg-gray-50/50 justify-center items-center w-32 relative">
                <div className="absolute -top-3 -left-3 size-6 bg-white border border-gray-100 rounded-full" />
                <div className="absolute -bottom-3 -left-3 size-6 bg-white border border-gray-100 rounded-full" />
                <div className="flex flex-col items-center gap-2">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">
                    Status
                  </span>
                  <span className="text-xs font-bold text-visaro-orange uppercase tracking-wider bg-visaro-orange/10 px-2 py-1 rounded">
                    Booked
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default BookedFlightsList;
